import { useCallback, useEffect, useRef } from 'react';
import Cookies from 'js-cookie';
import { getUID, createSessionContextEvent, parseGACookie } from '../analyticsUtils';
import { useAuth } from 'react-oidc-context';

const intervalInSeconds = 5;
const SESSION_CONTEXT_SENT_KEY = '_pli_session_context_sent';

// Send events to EventHubs reading window.eventHubDataLayer array every N seconds
export const useEventHubDataLayerConsumer = () => {
  const lastIndexProcessed = useRef<number>(0);
  const sessionContextSent = useRef(false);
  const pageLoadTime = useRef(Date.now());
  const auth = useAuth();
  const userId =
    (auth?.user?.profile?.IndividualSK as string)?.length > 0
      ? (auth.user?.profile?.IndividualSK as string)
      : '000000';
  const measurementId = import.meta.env.VITE_APP_GA4_MEASUREMENT_ID;
  const cookieName = `_gtm_ga_${measurementId}`;
  let gaCookie = Cookies.get(cookieName) || Cookies.get('_gtm_ga');
  if (!gaCookie || gaCookie === 'deleted') {
    gaCookie = Cookies.get(`_ga_${measurementId}`) || Cookies.get('_ga');
  }
  const { clientId, sessionNumber } = parseGACookie(gaCookie);
  const sessionGuid = gaCookie ? `${gaCookie}.${userId}` : 'NoSession';

  // Replaced async calls with `navigator.sendBeacon()` for reliable data sending during page unload.
  // `sendBeacon` ensures data is sent without being blocked by browser unload restrictions.
  const sendEvents = useCallback((events: string[]) => {
    const dataBlob = new Blob([JSON.stringify(events)], { type: 'application/json' });
    navigator.sendBeacon(
      `${import.meta.env.VITE_CONTENT_DELIVERY_API}/api/client/event-hub/events`,
      dataBlob
    );
  }, []);

  const sendSessionContext = useCallback(() => {
    const sessionStorageKey = `${SESSION_CONTEXT_SENT_KEY}_${sessionGuid}`;
    const alreadySent = sessionStorage.getItem(sessionStorageKey);

    if (!alreadySent && !sessionContextSent.current) {
      const contextEvent = createSessionContextEvent();

      window.eventHubDataLayer = window.eventHubDataLayer || [];
      window.eventHubDataLayer.push(contextEvent);

      sendEvents([JSON.stringify(contextEvent)]);
      sessionStorage.setItem(sessionStorageKey, 'true');
      sessionContextSent.current = true;
    }
  }, [sessionGuid, sendEvents]);

  const processEvents = useCallback(async () => {
    const currentLastIndex = window.eventHubDataLayer?.length;
    if (currentLastIndex > 0) {
      const eventsToBeProcessed =
        window.eventHubDataLayer.slice(lastIndexProcessed.current, currentLastIndex) ?? [];
      if (eventsToBeProcessed.length > 0) {
        try {
          const analyticsEventHash = getUID();
          const analyticsSentEventTimestamp = new Date().getTime();
          const engagementTime = Date.now() - pageLoadTime.current;
          const enrichedEvents = eventsToBeProcessed.map(function (event, index) {
            const newEvent = {
              ...event,
              client_id: clientId || 'unknown',
              ga_session_number: sessionNumber?.toString() || '1',
              analytics_event_hash: analyticsEventHash,
              analytics_sent_event_timestamp: analyticsSentEventTimestamp,
              engagement_time_msec: engagementTime,
              sessionGuid: sessionGuid,
              page_location: window.location.href,
              page_referrer: document.referrer,
              page_title: document.title,
              dataSource: 'FrontEnd'
            };

            window.eventHubDataLayer[lastIndexProcessed.current + index] = newEvent;

            return JSON.stringify(newEvent);
          });

          sendEvents(enrichedEvents);
          lastIndexProcessed.current = currentLastIndex;
        } catch (error) {
          // Handle error
        }
      }
    }
  }, [clientId, sendEvents, sessionGuid, sessionNumber]);

  useEffect(() => {
    sendSessionContext();

    const timerInterval = setInterval(async () => {
      processEvents();
    }, intervalInSeconds * 1000);

    return () => {
      clearInterval(timerInterval);
    };
  }, [processEvents, sendSessionContext]);

  return {
    processEvents
  };
};
